import {
  Box,
  Column,
  DeleteIcon,
  DownloadIcon,
  EditableText,
  IconButton,
  InformationIcon,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";
import {
  LineChartLoader,
  HeatMapChartLoader,
} from "src/components/charts/loaders";
import { Card } from "src/components/card";

import type {
  ChartConfig,
  ChartResult,
  LineChartResult,
  GridChartResult,
  LabelResolver,
  MetricDenominator,
  MetricNumerator,
} from "./types";
import { SendInsightsLineChart } from "./line-chart";
import { SendInsightsGridChart } from "./grid-chart";
import {
  getDenominatorSource,
  getMetricSourceTooltip,
  getNumeratorSource,
} from "./metric-source";
import { exportGridChartAsCsv } from "./export-csv";

function usesCampaignGroup(config: ChartConfig): boolean {
  return (
    config.segment_by === "campaign_group" ||
    config.segment_by_2 === "campaign_group" ||
    (config.filters.campaign_groups?.length ?? 0) > 0
  );
}

interface ChartCardProps {
  config: ChartConfig;
  result: ChartResult | undefined;
  isComputing: boolean;
  onDelete: () => void;
  onRename?: (name: string) => void;
  isDeleting: boolean;
  resolveLabel: LabelResolver;
  hasCampaignId: boolean;
  flowName: string;
}

export function ChartCard({
  config,
  result,
  isComputing,
  onDelete,
  onRename,
  isDeleting,
  resolveLabel,
  hasCampaignId,
  flowName,
}: ChartCardProps) {
  const needsCampaignId = usesCampaignGroup(config) && !hasCampaignId;
  const isGridResult = result?.type === "grid";
  const isDownloadable = config.downloadable;
  const canDownloadCsv =
    isDownloadable &&
    isGridResult &&
    (result as GridChartResult).cells.length > 0;

  const headingContent =
    config.source === "user" && onRename ? (
      <EditableText
        value={config.name}
        onChange={onRename}
        fontWeight="medium"
        size="md"
      />
    ) : (
      <Text fontWeight="medium" size="md">
        {config.name}
      </Text>
    );

  const rightContent = (
    <Row gap={2} align="center">
      {config.type === "grid" && config.window_days && (
        <Text size="sm" color="text.secondary">
          Last {config.window_days} days
        </Text>
      )}
      {isDownloadable && (
        <Tooltip placement="top" message="Download CSV">
          <IconButton
            aria-label="Download CSV"
            icon={DownloadIcon}
            size="sm"
            isDisabled={!canDownloadCsv}
            onClick={() => {
              if (!canDownloadCsv) return;
              exportGridChartAsCsv({
                flowName,
                config,
                result: result as GridChartResult,
                resolveLabel,
              });
            }}
          />
        </Tooltip>
      )}
      {config.source !== "system" && (
        <IconButton
          aria-label="Delete chart"
          icon={DeleteIcon}
          variant="danger"
          size="sm"
          onClick={onDelete}
          isLoading={isDeleting}
        />
      )}
    </Row>
  );

  return (
    <Card gap={4} overflow="hidden">
      <Row justify="space-between" align="start">
        <Column gap={0} flex="1" minWidth={0}>
          {headingContent}
          <MetricSourceLine
            numerator={config.numerator}
            denominator={config.denominator}
            isCustom={config.source !== "system"}
          />
          {(() => {
            const description = config.description;
            return description ? (
              <Text size="sm" color="text.secondary" mt={1}>
                {description}
              </Text>
            ) : null;
          })()}
        </Column>
        {rightContent}
      </Row>
      <Box>
        {needsCampaignId ? (
          <Row justify="center" py={8}>
            <Text color="text.secondary">
              Campaign group breakdown requires a campaign ID column configured
              on your channel's send event settings.
            </Text>
          </Row>
        ) : isComputing ? (
          <Box py={4}>
            {config.type === "grid" ? (
              <HeatMapChartLoader />
            ) : (
              <LineChartLoader />
            )}
          </Box>
        ) : result ? (
          renderChart(config, result, resolveLabel)
        ) : (
          <Row justify="center" py={8}>
            <Text color="text.secondary">No data available</Text>
          </Row>
        )}
      </Box>
    </Card>
  );
}

function MetricSourceLine({
  numerator,
  denominator,
  isCustom,
}: {
  numerator: MetricNumerator;
  denominator: MetricDenominator;
  isCustom: boolean;
}) {
  const num = getNumeratorSource(numerator);
  const denom = getDenominatorSource(denominator);
  const tooltipMessage = getMetricSourceTooltip(numerator, denominator);

  return (
    <Row align="center" gap={1}>
      <Text size="sm" color="text.tertiary">
        {num.shortLabel}
        {denom ? ` / ${denom.shortLabel}` : ""}
      </Text>
      <Tooltip placement="top" message={tooltipMessage}>
        <Box
          display="inline-flex"
          alignItems="center"
          color="text.tertiary"
          cursor="help"
        >
          <InformationIcon />
        </Box>
      </Tooltip>
      {isCustom && (
        <Text size="sm" color="text.tertiary">
          · Custom chart
        </Text>
      )}
    </Row>
  );
}

function renderChart(
  config: ChartConfig,
  result: ChartResult,
  resolveLabel: LabelResolver,
) {
  if (result.type === "line") {
    return (
      <SendInsightsLineChart
        config={config}
        result={result as LineChartResult}
        resolveLabel={resolveLabel}
      />
    );
  }
  return (
    <SendInsightsGridChart
      config={config}
      result={result as GridChartResult}
      resolveLabel={resolveLabel}
    />
  );
}
